import {queue, QueueObject} from 'async';
import {
  BrowserMessageType,
  exchangeElectronMessage,
} from 'electron-communication-lib/BrowserForApp';
import {makeAutoObservable, runInAction} from 'mobx';
import {v4 as uuid} from 'uuid';
import {showSnackbar, SnackbarType} from 'webapp-lib/Functions';

import handledDocumentsBackend from '@lexian/api/documents-api';
import i18n from '_/i18n';
import {DocumentFileUpload} from '_/store/DocumentFileUpload';
import {isElectron} from '_/utilities';
import {getFileType} from '_/utilities/file-handling';
import {
  ElectronFile,
  FileWithMeta,
  isNotNullOrUndefined,
} from '_/utilities/types';

const MAX_FILES_FOR_UPLOAD = 1000;
const MAX_PARALLEL_UPLOAD_FILES = 2;
const SLEEP_BETWEEN_UPLOADS_IN_MS = 250;

export type UploadDestination = {
  casusId: string;
  casusDisplayNameWithClient: string;
  folderId: string;
};

export type DocumentFileUploadsByState = {
  pending: DocumentFileUpload[];
  successful: DocumentFileUpload[];
  failed: DocumentFileUpload[];
};

export class DocumentUploadStore {
  public queue: QueueObject<DocumentFileUpload>;
  public pendingFiles: DocumentFileUpload[] = [];
  public successfulFiles: DocumentFileUpload[] = [];
  public failedFiles: DocumentFileUpload[] = [];

  private isOnlyUploadingOneFile = false;

  public constructor() {
    makeAutoObservable(this, {}, {autoBind: true});
    this.queue = queue(
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      async (documentFileUpload: DocumentFileUpload, callback: () => void) => {
        await this.uploadFile(documentFileUpload);
        callback();
      },
      MAX_PARALLEL_UPLOAD_FILES
    );

    this.queue.drain(() => {
      if (this.isOnlyUploadingOneFile) {
        this.isOnlyUploadingOneFile = false;
        return;
      }

      // This can happen if all uploads were aborted
      if (this.failedFiles.length === 0 && this.successfulFiles.length === 0) {
        return;
      }

      if (this.failedFiles.length === 0) {
        showSnackbar({
          text: i18n.t('documents:snackbar.uploadMultipleDocument.success'),
        });
        return;
      }

      if (this.successfulFiles.length > 0) {
        showSnackbar({
          text: i18n.t(
            'documents:snackbar.uploadMultipleDocument.successAndFailed',
            {failedAmount: this.failedFiles.length}
          ),
          type: SnackbarType.INFO,
        });
        return;
      }

      showSnackbar({
        text: i18n.t('documents:snackbar.uploadMultipleDocument.failed', {
          failedAmount: this.failedFiles.length,
        }),
        type: SnackbarType.WARNING,
      });
    });
  }

  public get documentFileUploads(): DocumentFileUploadsByState {
    return {
      pending: this.pendingFiles,
      successful: this.successfulFiles,
      failed: this.failedFiles,
    };
  }

  public fillMetadataAndUploadFiles(
    files: ElectronFile[],
    destination: UploadDestination,
    onSuccess: (documentIds: string[]) => void
  ): void {
    const handleUploadSuccess = (documentIds: string[]) => {
      // Warning: this may trigger twice if the upload is fast enough
      if (documentIds.length === files.length) {
        onSuccess(documentIds);
      }
    };

    if (isElectron()) {
      exchangeElectronMessage(
        {
          payload: {
            filePaths: files
              .map((file) => file.path)
              .filter((path): path is string => Boolean(path)),
          },
          type: BrowserMessageType.SYNC_UPLOAD_FILE_META_DATA,
        },
        (filesWithMeta) => {
          const filesToUpload: FileWithMeta[] = filesWithMeta
            .map(({filePath, createdAt, modifiedAt}) => {
              const file = files.find((f) => f.path === filePath);
              if (file) {
                return createdAt && modifiedAt
                  ? Object.assign(file, {createdAt, modifiedAt})
                  : file;
              }
              return null;
            })
            .filter(isNotNullOrUndefined);
          this.uploadFiles(filesToUpload, destination, handleUploadSuccess);
        }
      );
    } else {
      this.uploadFiles(files, destination, handleUploadSuccess);
    }
  }

  private uploadFiles(
    files: FileWithMeta[],
    destination: UploadDestination,
    onSuccess: (documentIds: string[]) => void
  ): void {
    const {folderId} = destination;
    if (!folderId) {
      throw new Error('Missing folderId for uploadFiles');
    }

    if (files.length > MAX_FILES_FOR_UPLOAD) {
      showSnackbar({
        text: i18n.t('documents:snackbar.uploadDocument.maxFiles', {
          maxFiles: MAX_FILES_FOR_UPLOAD,
        }),
        type: SnackbarType.WARNING,
      });
      return;
    }

    const documentFileUploads = files.map((file) => {
      const id = uuid();
      return new DocumentFileUpload({
        id,
        name: file.name,
        casusId: destination.casusId,
        casusDisplayNameWithClient: destination.casusDisplayNameWithClient,
        file,
        folderId,
        abort: () => this.removeFromPendingFiles(id),
      });
    });
    this.pendingFiles = [...this.pendingFiles, ...documentFileUploads];
    this.isOnlyUploadingOneFile = this.pendingFiles.length === 1;

    this.queue.push(documentFileUploads, () => {
      const successfulUploads = documentFileUploads.filter(
        (upload) => upload.progress === 1 && !upload.aborted
      );
      if (documentFileUploads.length === 1 && successfulUploads.length === 1) {
        showSnackbar({
          text: i18n.t('documents:snackbar.uploadDocument.success', {
            name: documentFileUploads[0].name,
          }),
        });
      }
      onSuccess(successfulUploads.map(({id}) => id));
    });
  }

  private async uploadFile(
    documentFileUpload: DocumentFileUpload
  ): Promise<void> {
    // Files can be aborted if they were aborted while in the queue, but before the upload started
    if (documentFileUpload.aborted) {
      return;
    }

    const contentType = getFileType(documentFileUpload.file);
    await handledDocumentsBackend.createDocument({
      parameters: {
        // Last 0.05 of the progress is for finalizing the request which takes a little time after upload is complete
        // (Backend takes some time to respond after it gets the complete request)
        $progressCallback: ({percent}) =>
          documentFileUpload.setProgress(
            percent === undefined ? 0 : (percent / 100) * 0.95
          ),
        casusId: documentFileUpload.casusId,
        createdAt: documentFileUpload.file.createdAt,
        modifiedAt: documentFileUpload.file.modifiedAt,
        file: new File([documentFileUpload.file], documentFileUpload.name, {
          type: contentType,
        }),
        folderId: documentFileUpload.folderId,
      },
      onSuccess: ({body}) => {
        runInAction(() => {
          documentFileUpload.setProgress(1);
          documentFileUpload.setCreatedDocumentId(body.createdDocument.id);
          this.movePendingToSuccessful(documentFileUpload);
        });
      },
      localHandlers: {
        defaultErrorCondition: () => {
          runInAction(() => {
            this.movePendingToFailed(documentFileUpload);
          });
        },
      },
      abortCallbackSetter: (abort) => {
        documentFileUpload.setAbort(() => {
          abort();
          runInAction(() => {
            this.removeFromPendingFiles(documentFileUpload.id);
          });
        });
      },
    });

    // sleep some minimal time to no stress the server too much if there are a lot of small files
    await new Promise((r) => setTimeout(r, SLEEP_BETWEEN_UPLOADS_IN_MS));
  }

  private removeFromPendingFiles(documentFileUploadId: string): void {
    this.pendingFiles = [
      ...this.pendingFiles.filter(({id}) => id !== documentFileUploadId),
    ];
  }

  private movePendingToSuccessful(
    documentFileUpload: DocumentFileUpload
  ): void {
    this.removeFromPendingFiles(documentFileUpload.id);
    this.successfulFiles = [...this.successfulFiles, documentFileUpload];
  }

  private movePendingToFailed(documentFileUpload: DocumentFileUpload): void {
    this.removeFromPendingFiles(documentFileUpload.id);
    this.failedFiles = [...this.failedFiles, documentFileUpload];
  }

  public clearAllSuccessfulUploads(): void {
    this.successfulFiles = [];
  }

  public clearAllFailedUploads(): void {
    this.failedFiles = [];
  }

  public abortAllPendingUploads(): void {
    this.pendingFiles.forEach((upload) => upload.abort());
  }

  public retryUpload(documentFileUpload: DocumentFileUpload): void {
    this.failedFiles = this.failedFiles.filter(
      ({id}) => id !== documentFileUpload.id
    );
    this.pendingFiles = [documentFileUpload, ...this.pendingFiles];
    this.queue.unshift(documentFileUpload);
  }
}
